import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  AcademicCap,
  Add,
  AlertCircle,
  Delete,
  Download,
  Files,
  Pencil,
  Person,
} from '@factorialco/f0-react/icons/app'
import illustration from 'assets/images/competencies/competencies.png'
import {
  CompetenciesDataCollectionCompetencyFragment,
  CompetenciesDataCollectionDocument,
  CompetenciesDataCollectionGroupFragment,
  CompetenciesDataCollectionJobCatalogRoleFragment,
  CompetenciesDuplicateCompetencyDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteCompetencyModal } from 'modules/competencies/components/CompetencyDeleteModal'
import { EditCompetencyModal } from 'modules/competencies/components/EditCompetencyModal'
import { useCompetenciesFeatures } from 'modules/competencies/hooks/useCompetenciesFeatures'

import * as styles from './index.vanilla.css'

const getAudienceDisplayValue = (item: {
  everyone: boolean
  roles: { name: string }[]
}): string => {
  if (item.everyone) {
    return i18n.t('competencies_v3.competencies_table.audience.everyone')
  }
  if (item.roles.length === 0) {
    return i18n.t('competencies_v3.competencies_table.audience.no_jobs')
  }
  if (item.roles.length === 1) {
    return item.roles[0]?.name || ''
  }
  return i18n.t('competencies_v3.competencies_table.audience.many_jobs', {
    count: item.roles.length,
  })
}

interface Props {
  competencyGroups: CompetenciesDataCollectionGroupFragment[]
  jobCatalogRoles: CompetenciesDataCollectionJobCatalogRoleFragment[]
  canManageCompetencies: boolean
  canImportCompetencies: boolean
  canBulkAssignCompetencies: boolean
  canSeeJobCatalogTree: boolean
  onNewCompetency: () => void
}

export const CompetenciesDataCollection = ({
  competencyGroups,
  jobCatalogRoles,
  canManageCompetencies,
  canImportCompetencies,
  canBulkAssignCompetencies,
  canSeeJobCatalogTree,
  onNewCompetency,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { isJobCatalogTreeEnabled } = useCompetenciesFeatures()

  const [totalItems, setTotalItems] = useState<number | null>(null)
  const [competencyToDelete, setCompetencyToDelete] =
    useState<CompetenciesDataCollectionCompetencyFragment | null>(null)
  const [competencyToEdit, setCompetencyToEdit] =
    useState<CompetenciesDataCollectionCompetencyFragment | null>(null)

  const [duplicateCompetency] = useMutation(CompetenciesDuplicateCompetencyDocument, {
    extractorFn: (data) => data.competenciesMutations.duplicateCompetenciesCompetency,
  })

  const handleEditCompetency = (competency: CompetenciesDataCollectionCompetencyFragment) => {
    if (isJobCatalogTreeEnabled) {
      setCompetencyToEdit(competency)
    } else {
      navigate(
        navigation.competencies.competencyId.settings.fullPath({
          competencyId: competency.id,
        })
      )
    }
  }

  const handleDuplicate = async (competency: CompetenciesDataCollectionCompetencyFragment) => {
    const result = await duplicateCompetency({
      variables: { id: competency.id },
      refetchQueries: [CompetenciesDataCollectionDocument],
    })

    const handled = handleResult(result, () => {})

    if (handled?.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('competencies_v3.competencies_table.actions.duplicate.success'),
      })
    } else if (handled?.errors?.length > 0) {
      const error = handled.errors[0]
      if (
        error?.__typename === 'StructuredError' &&
        error.messages?.length &&
        error.messages[0] === 'has already been taken'
      ) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('competencies_v3.competencies_table.actions.duplicate.error_duplicated'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('competencies_v3.competencies_table.actions.duplicate.error'),
        })
      }
    }

    return handled
  }

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: true,
    document: CompetenciesDataCollectionDocument,
    mapData: (data) => data.competencies.competenciesConnection.nodes,
    mapPagination: (data) => {
      const total = data.competencies.competenciesConnection.totalCount ?? 0
      setTotalItems(total)

      return { total }
    },
    mapVariables: ({ filters, search, sortings }) => ({
      search,
      competencyGroupIds: filters.competencyGroupIds,
      roleIds: !isJobCatalogTreeEnabled ? filters.roleIds : undefined,
      sort: sortings?.[0],
      includeRoleLevels: isJobCatalogTreeEnabled && canSeeJobCatalogTree,
      includeCompetencyLevels: isJobCatalogTreeEnabled,
    }),
    filters: {
      competencyGroupIds: {
        label: i18n.t('competencies_v3.competencies_table.columns.group'),
        type: 'in',
        options: {
          options: () => {
            return competencyGroups.map((group) => ({
              label: group.name,
              value: group.id,
            }))
          },
        },
      },
      ...(isJobCatalogTreeEnabled && {
        roleIds: {
          label: i18n.t('competencies_v3.competencies_table.filters.job_role'),
          type: 'in',
          options: {
            options: () => {
              return jobCatalogRoles.map((role) => ({
                label: role.name,
                value: role.id,
              }))
            },
          },
        },
      }),
    },
    sortings: {
      name: { label: i18n.t('competencies_v3.competencies_table.columns.name') },
    },
    defaultSorting: {
      field: 'name',
      order: 'asc',
    },
    search: {
      enabled: true,
    },
    primaryActions: () =>
      canManageCompetencies
        ? {
            label: i18n.t('competencies_v3.competencies_table.empty_state.create_button'),
            icon: Add,
            onClick: () => onNewCompetency(),
          }
        : undefined,
    secondaryActions: {
      expanded: canBulkAssignCompetencies && totalItems ? 1 : 0,
      actions: () =>
        compact([
          canBulkAssignCompetencies &&
            totalItems && {
              label: i18n.t('competencies_v3.competencies_table.actions.assign_jobs'),
              icon: AcademicCap,
              expanded: true,
              onClick: () =>
                navigate(navigation.employees.jobCatalog.bulkAssignCompetencies.fullPath()),
            },
          canImportCompetencies && {
            label: i18n.t('competencies_v3.import'),
            icon: Download,
            onClick: () => navigate(navigation.competencies.import.fullPath()),
          },
        ]),
    },
    itemActions: (item) => {
      if (!canManageCompetencies) return []

      const actions = [
        {
          label: i18n.t('common.edit'),
          icon: Pencil,
          onClick: () => handleEditCompetency(item),
        },
        {
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => setCompetencyToDelete(item),
          critical: true,
        },
      ]

      if (!isJobCatalogTreeEnabled) {
        actions.push({
          label: i18n.t('common.duplicate'),
          icon: Files,
          onClick: () => handleDuplicate(item),
        })
      }

      return actions
    },
    itemUrl: (item) =>
      isJobCatalogTreeEnabled
        ? navigation.competencies.competencyId.levels.fullPath({ competencyId: item.id })
        : undefined,
    perPage: 20,
  })

  const tableColumns = compact([
    {
      label: i18n.t('competencies_v3.competencies_table.columns.name'),
      render: (item: CompetenciesDataCollectionCompetencyFragment) => ({
        type: 'text' as const,
        value: item.name,
      }),
      sorting: 'name' as const,
    },
    {
      label: i18n.t('competencies_v3.competencies_table.columns.group'),
      render: (item: CompetenciesDataCollectionCompetencyFragment) => ({
        type: 'text' as const,
        value: item.competencyGroup?.name || '-',
      }),
    },
    !isJobCatalogTreeEnabled && {
      label: i18n.t('competencies_v3.competencies_table.columns.audience'),
      render: (item: CompetenciesDataCollectionCompetencyFragment) => ({
        type: 'text' as const,
        value: getAudienceDisplayValue(item),
      }),
    },
    isJobCatalogTreeEnabled && {
      label: i18n.t('competencies_v3.competencies_table.columns.levels'),
      render: ({ competencyLevelsConnection }: CompetenciesDataCollectionCompetencyFragment) => ({
        type: 'text' as const,
        value: String(competencyLevelsConnection?.totalCount ?? 0),
      }),
    },
    isJobCatalogTreeEnabled &&
      canSeeJobCatalogTree && {
        label: i18n.t('competencies_v3.competencies_table.columns.jobs_assigned'),
        render: ({ jobCatalogRoleLevels }: CompetenciesDataCollectionCompetencyFragment) => {
          const roleLevelsCount = jobCatalogRoleLevels?.nodeUuids.length ?? 0
          const hasLevels = roleLevelsCount > 0

          return {
            type: hasLevels ? ('tag' as const) : ('status' as const),
            value: {
              label: hasLevels
                ? i18n.tp('competencies_v3.competencies_table.columns.jobs_count', {
                    count: roleLevelsCount,
                  })
                : i18n.t('competencies_v3.competencies_table.columns.no_jobs_assigned'),
              icon: hasLevels ? Person : AlertCircle,
              status: hasLevels ? 'neutral' : 'critical',
            },
          }
        },
      },
  ])

  return (
    <>
      <OneDataCollection
        fullHeight={!!totalItems}
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('competencies_v3.competencies_table.empty_state.title'),
            description: i18n.t('competencies_v3.competencies_table.empty_state.description'),
            actions: compact([
              canManageCompetencies && {
                label: i18n.t('competencies_v3.competencies_table.empty_state.create_button'),
                icon: Add,
                onClick: () => onNewCompetency(),
              },
              canImportCompetencies && {
                label: i18n.t('competencies_v3.import'),
                icon: Download,
                variant: 'outline',
                onClick: () => navigate(navigation.competencies.import.fullPath()),
              },
            ]),
          },
        }}
        visualizations={[{ type: 'table', options: { columns: tableColumns, frozenColumns: 1 } }]}
      />
      {totalItems === 0 && (
        <Box paddingTop="s24" alignItems="center" justifyContent="center">
          <img
            src={illustration}
            alt={i18n.t('competencies_v3.set_up_competencies.illustration_alt')}
            title={i18n.t('competencies_v3.set_up_competencies.illustration_alt')}
            className={styles.illustration}
          />
        </Box>
      )}
      {competencyToDelete && (
        <DeleteCompetencyModal
          competencyId={competencyToDelete.id}
          competencyName={competencyToDelete.name}
          onClose={() => setCompetencyToDelete(null)}
        />
      )}
      {competencyToEdit && (
        <EditCompetencyModal
          competency={competencyToEdit}
          onClose={() => setCompetencyToEdit(null)}
        />
      )}
    </>
  )
}
